/**
 * Modern Lazy Loading Script
 * Uses Intersection Observer API for efficient lazy loading
 * No history manipulation - safe for browser back button
 */

(function () {
  "use strict";

  // Check if Intersection Observer is supported
  if (!("IntersectionObserver" in window)) {
    // Fallback: load everything immediately for older browsers
    loadAllDeferred();
    return;
  }

  // Configuration
  const config = {
    rootMargin: "50px 0px", // Start loading 50px before element enters viewport
    threshold: 0.01,
  };

  // Track if scripts have been loaded to prevent duplicate loading
  let scriptsLoaded = false;

  /**
   * Lazy load images and iframes with data-src attributes
   */
  function initLazyMedia() {
    const lazyElements = document.querySelectorAll(
      "img[data-src], iframe[data-src], source[data-src]"
    );

    if (lazyElements.length === 0) return;

    const mediaObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const src = element.dataset.src;

          if (src) {
            element.src = src;
            element.removeAttribute("data-src");

            // For images, remove lazy class and add loaded class
            if (element.tagName === "IMG") {
              element.classList.remove("lazy");
              element.classList.add("lazy-loaded");
            }
          }

          observer.unobserve(element);
        }
      });
    }, config);

    lazyElements.forEach((element) => mediaObserver.observe(element));
  }

  /**
   * Lazy load background images with data-bg attributes
   */
  function initLazyBackgrounds() {
    const lazyBackgrounds = document.querySelectorAll("[data-bg]");

    if (lazyBackgrounds.length === 0) return;

    const bgObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const bg = element.dataset.bg;

          if (bg) {
            element.style.backgroundImage = `url(${bg})`;
            element.removeAttribute("data-bg");
          }

          observer.unobserve(element);
        }
      });
    }, config);

    lazyBackgrounds.forEach((element) => bgObserver.observe(element));
  }

  /**
   * Load deferred scripts (scripts with type="text/lazyload" or data-src)
   */
  function loadDeferredScripts() {
    if (scriptsLoaded) return;
    scriptsLoaded = true;

    // Load scripts with type="text/lazyload"
    const lazyScripts = document.querySelectorAll(
      'script[type="text/lazyload"]'
    );
    lazyScripts.forEach((oldScript) => {
      const newScript = document.createElement("script");

      // Copy attributes
      Array.from(oldScript.attributes).forEach((attr) => {
        if (attr.name !== "type") {
          newScript.setAttribute(attr.name, attr.value);
        }
      });

      newScript.type = "text/javascript";

      // Copy inline content or src
      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }

      oldScript.parentNode.insertBefore(newScript, oldScript);
      oldScript.parentNode.removeChild(oldScript);
    });

    // Load scripts with data-src
    const dataSrcScripts = document.querySelectorAll("script[data-src]");
    dataSrcScripts.forEach((script) => {
      const src = script.dataset.src;
      if (src) {
        script.src = src;
        script.removeAttribute("data-src");
      }
    });

    // Load stylesheets with data-href
    const lazyLinks = document.querySelectorAll("link[data-href]");
    lazyLinks.forEach((link) => {
      const href = link.dataset.href;
      if (href) {
        link.href = href;
        link.removeAttribute("data-href");
      }
    });

    // Dispatch custom event for compatibility
    document.dispatchEvent(new CustomEvent("asyncLazyLoad"));

    // Dispatch secondary event after short delay
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent("loadBarInjector"));
    }, 1000);
  }

  /**
   * Fallback: Load all deferred content immediately
   */
  function loadAllDeferred() {
    // Load all images
    document
      .querySelectorAll("img[data-src], iframe[data-src], source[data-src]")
      .forEach((el) => {
        if (el.dataset.src) {
          el.src = el.dataset.src;
          el.removeAttribute("data-src");
        }
      });

    // Load all backgrounds
    document.querySelectorAll("[data-bg]").forEach((el) => {
      if (el.dataset.bg) {
        el.style.backgroundImage = `url(${el.dataset.bg})`;
        el.removeAttribute("data-bg");
      }
    });

    // Load scripts
    loadDeferredScripts();
  }

  /**
   * Initialize on first user interaction (for deferred scripts only)
   * Images/iframes load based on viewport intersection
   */
  function initOnInteraction() {
    const events = ["mousedown", "touchstart", "keydown", "scroll"];

    function loadOnce() {
      loadDeferredScripts();
      // Remove event listeners after first trigger
      events.forEach((event) => {
        window.removeEventListener(event, loadOnce, {passive: true});
      });
    }

    events.forEach((event) => {
      window.addEventListener(event, loadOnce, {passive: true, once: true});
    });
  }

  /**
   * Initialize lazy loading
   */
  function init() {
    // Always initialize lazy media loading (viewport-based)
    initLazyMedia();
    initLazyBackgrounds();

    // Defer script loading until user interaction
    initOnInteraction();

    // Also load scripts after 3 seconds if no interaction
    setTimeout(loadDeferredScripts, 3000);
  }

  // Start when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
